import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { makeApi } from "@/lib/api";
import { AddDriverDialog } from "@/components/drivers/AddDriverDialog";
import { EditDriverDialog } from "@/components/drivers/EditDriverDialog";
import { CredentialsDialog } from "@/components/drivers/CredentialsDialog";
import { DriverRow } from "@/components/drivers/DriverRow";

const errMsg = (e) => e?.response?.data?.detail?.toString() || "Something went wrong";

export default function Drivers() {
  const { getToken } = useAuth();
  const api = makeApi(getToken);
  const [drivers, setDrivers] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [archiving, setArchiving] = useState(null);
  const [creds, setCreds] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/drivers");
      setDrivers(data);
    } catch (e) {
      setDrivers((prev) => prev ?? []);
      toast.error(`Could not load drivers: ${errMsg(e)}`);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const addDriver = async (form) => {
    try {
      await api.post("/admin/drivers", { ...form, email: form.email.trim() });
      setAdding(false);
      setCreds({ title: "Driver created", email: form.email.trim().toLowerCase(), password: form.password });
      refresh();
      return null;
    } catch (e) { return errMsg(e); }
  };

  const saveEdit = async (value) => {
    const { driver, mode } = editing;
    try {
      if (mode === "name") {
        await api.patch(`/admin/drivers/${driver.user_id}`, { name: value });
        toast.success("Driver updated");
      } else {
        await api.post(`/admin/drivers/${driver.user_id}/password`, { password: value });
        setCreds({ title: "Password reset", email: driver.email, password: value });
      }
      setEditing(null);
      refresh();
      return null;
    } catch (e) { return errMsg(e); }
  };

  const archive = async () => {
    try {
      const { data } = await api.post(`/admin/drivers/${archiving.user_id}/archive`);
      toast.success(`${archiving.name} archived${data.unassigned_loads ? ` · ${data.unassigned_loads} load(s) unassigned` : ""}`);
      setArchiving(null);
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const restore = async (d) => {
    try {
      await api.post(`/admin/drivers/${d.user_id}/restore`);
      toast.success(`${d.name} restored`);
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="drivers-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>Drivers</h1>
          <p className="text-sm text-slate-500 mt-1">Add drivers and hand them their login. They only see loads you assign them.</p>
        </div>
        <Button onClick={() => setAdding(true)} className="bg-[#002FA7] hover:bg-[#001A75] rounded-full" data-testid="add-driver-btn">
          <UserPlus className="w-4 h-4 mr-1.5" /> Add driver
        </Button>
      </div>

      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Name</th>
                <th className="hidden md:table-cell text-left px-4 py-3 font-medium">Email</th>
                <th className="hidden sm:table-cell text-left px-4 py-3 font-medium">Active loads</th>
                <th className="hidden lg:table-cell text-left px-4 py-3 font-medium">Added</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {drivers === null && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>}
              {drivers?.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400" data-testid="drivers-empty">No drivers yet. Tap “Add driver” to create the first login.</td></tr>
              )}
              {drivers?.map((d) => (
                <DriverRow
                  key={d.user_id}
                  driver={d}
                  onEdit={(driver) => setEditing({ driver, mode: "name" })}
                  onReset={(driver) => setEditing({ driver, mode: "password" })}
                  onArchive={setArchiving}
                  onRestore={restore}
                />
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <AddDriverDialog open={adding} onClose={() => setAdding(false)} onSubmit={addDriver} />
      <EditDriverDialog driver={editing?.driver} mode={editing?.mode} onClose={() => setEditing(null)} onSubmit={saveEdit} />
      <CredentialsDialog creds={creds} onClose={() => setCreds(null)} />

      <AlertDialog open={!!archiving} onOpenChange={(v) => !v && setArchiving(null)}>
        <AlertDialogContent data-testid="archive-driver-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {archiving?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              They will be removed from the driver lists and taken off {archiving?.active_loads || 0} unfinished load(s).
              Delivered and invoiced loads keep their name. You can restore them later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="archive-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={archive} className="bg-rose-600 hover:bg-rose-700" data-testid="archive-confirm-btn">Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
